import { StructuredToolInterface } from '@langchain/core/tools';
import {
  companyResearch,
  concallSourcePack,
  corporateActionMonitor,
  createGetFinancials,
  createGetMarketData,
  createReadFilings,
  earningsAnalysis,
  earningsMetricComparison,
  eventCalendar,
  filingSourcePack,
  governanceMonitor,
  GET_FINANCIALS_DESCRIPTION,
  GET_MARKET_DATA_DESCRIPTION,
  READ_FILINGS_DESCRIPTION,
  marketPulse,
  ordersCapexTracker,
  peerGroupResearch,
  portfolioBrief,
  priceVolumeSignalExplainer,
  resultSeasonTracker,
  riskMonitor,
} from './finance/index.js';
import { exaSearch, perplexitySearch, tavilySearch, langSearch, WEB_SEARCH_DESCRIPTION, xSearchTool, X_SEARCH_DESCRIPTION } from './search/index.js';
import { createWebSearchTool, type WebSearchProvider } from './search/web-search.js';
import { getSetting } from '../utils/config.js';
import { checkApiKeyExists, type SearchProviderId } from '../utils/env.js';
import { skillTool, SKILL_TOOL_DESCRIPTION } from './skill.js';
import { createWebFetch, WEB_FETCH_DESCRIPTION } from './fetch/web-fetch.js';
import { browserTool, BROWSER_DESCRIPTION } from './browser/browser.js';
import { readFileTool, READ_FILE_DESCRIPTION } from './filesystem/read-file.js';
import { writeFileTool, WRITE_FILE_DESCRIPTION } from './filesystem/write-file.js';
import { editFileTool, EDIT_FILE_DESCRIPTION } from './filesystem/edit-file.js';
import { heartbeatTool, HEARTBEAT_TOOL_DESCRIPTION } from './heartbeat/heartbeat-tool.js';
import { cronTool, CRON_TOOL_DESCRIPTION } from './cron/cron-tool.js';
import { memoryGetTool, MEMORY_GET_DESCRIPTION, memorySearchTool, MEMORY_SEARCH_DESCRIPTION, memoryUpdateTool, MEMORY_UPDATE_DESCRIPTION } from './memory/index.js';
import { discoverSkills } from '../skills/index.js';
import { createSpawnSubagent, SPAWN_SUBAGENT_DESCRIPTION } from './subagent/spawn-subagent.js';
import { createAskUserQuestion, ASK_USER_QUESTION_DESCRIPTION } from './ask-user-question/ask-user-question.js';
import { isMcpConfigured } from './finance/drishti-mcp-client.js';
import { createSearchToolsTool, SEARCH_TOOLS_DESCRIPTION } from './finance/mcp-search-tools.js';
import { createAccountStatusTool, ACCOUNT_STATUS_DESCRIPTION } from './finance/drishti-account.js';

/**
 * A registered tool with its rich description for system prompt injection.
 */
export interface RegisteredTool {
  /** Tool name (must match the tool's name property) */
  name: string;
  /** The actual tool instance */
  tool: StructuredToolInterface;
  /** Rich description for system prompt (includes when to use, when not to use, etc.) */
  description: string;
  /** 1-2 sentence description for token-optimized system prompts. */
  compactDescription: string;
  /** Whether this tool can safely execute concurrently with other concurrent-safe tools. */
  concurrencySafe: boolean;
}

/**
 * Get all registered tools with their descriptions.
 * Conditionally includes tools based on environment configuration.
 *
 * @param model - The model name (needed for tools that require model-specific configuration)
 * @returns Array of registered tools
 */
export function getToolRegistry(model: string): RegisteredTool[] {
  const tools: RegisteredTool[] = [
    {
      name: 'company_research',
      tool: companyResearch,
      description: 'Build a Drishti company dossier for one Indian listed equity across metadata, news, important announcements, earnings, concalls, alerts, and upcoming events.',
      compactDescription: 'Drishti company dossier for one Indian equity: metadata, news, filings, earnings, concalls, alerts.',
      concurrencySafe: true,
    },
    {
      name: 'earnings_analysis',
      tool: earningsAnalysis,
      description: 'Build a Drishti results-analysis pack for an Indian company and optional quarter, combining earnings, concalls, transcripts, result announcements, news, and alerts.',
      compactDescription: 'Drishti earnings pack: filings, earnings tables, concalls, transcript URLs, result news/alerts.',
      concurrencySafe: true,
    },
    {
      name: 'earnings_metric_comparison',
      tool: earningsMetricComparison,
      description: 'Compare Drishti structured earnings_table metrics across Indian companies and/or quarters while preserving metric keys, units, values, and growth fields.',
      compactDescription: 'Drishti earnings metric comparison: earnings_table metrics, units, growth fields, summaries, call context.',
      concurrencySafe: true,
    },
    {
      name: 'portfolio_brief',
      tool: portfolioBrief,
      description: 'Build a Drishti portfolio/watchlist brief using weighted daily summary, alerts, news, important announcements, upcoming events, and metadata.',
      compactDescription: 'Drishti portfolio/watchlist brief: weighted summary, alerts, news, filings, events, metadata.',
      concurrencySafe: true,
    },
    {
      name: 'peer_group_research',
      tool: peerGroupResearch,
      description: 'Build a Drishti peer-group research pack for supplied Indian equities using metadata classifications, market cap, earnings, concalls, news, announcements, alerts, and events.',
      compactDescription: 'Drishti peer-group pack: metadata classifications, market cap, earnings, concalls, news, filings, alerts.',
      concurrencySafe: true,
    },
    {
      name: 'market_pulse',
      tool: marketPulse,
      description: 'Build a Drishti Indian-market pulse from recent news, important announcements, alerts, upcoming earnings, upcoming concalls, and optional watchlist summary.',
      compactDescription: 'Drishti market/watchlist pulse: news, alerts, filings, upcoming earnings and concalls.',
      concurrencySafe: true,
    },
    {
      name: 'price_volume_signal_explainer',
      tool: priceVolumeSignalExplainer,
      description: 'Explain Indian equity price, volume, relative-volume, and 52-week Drishti alert signals using alerts, primary drivers, news, filings, and optional result context.',
      compactDescription: 'Drishti price/volume signal explainer: alerts, primary drivers, news, filings, optional results.',
      concurrencySafe: true,
    },
    {
      name: 'result_season_tracker',
      tool: resultSeasonTracker,
      description: 'Track Indian result season using Drishti earnings index, concall index, upcoming events, result announcements, and significant earnings details.',
      compactDescription: 'Drishti result-season tracker: reported earnings/calls, upcoming events, result filings, significant earnings.',
      concurrencySafe: true,
    },
    {
      name: 'concall_source_pack',
      tool: concallSourcePack,
      description: 'Resolve transcript/audio URLs for multiple Indian conference calls and pair them with Drishti concall summaries, earnings context, and result announcements.',
      compactDescription: 'Drishti concall source pack: batch transcript/audio URLs, call summaries, earnings and result context.',
      concurrencySafe: true,
    },
    {
      name: 'filing_source_pack',
      tool: filingSourcePack,
      description: 'Resolve Drishti attachment URLs for Indian exchange announcements and earnings filings, optionally discovering recent source-document ids by symbol, category, and quarter.',
      compactDescription: 'Drishti filing source pack: discover announcement/earnings ids and resolve attachment URLs.',
      concurrencySafe: true,
    },
    {
      name: 'orders_capex_tracker',
      tool: ordersCapexTracker,
      description: 'Track Indian order wins, tenders, capex/expansion, partnerships, regulatory approvals, and business-development updates using Drishti announcements with execution context.',
      compactDescription: 'Drishti orders/capex tracker: orders, tenders, expansion, partnerships, approvals, execution context.',
      concurrencySafe: true,
    },
    {
      name: 'corporate_action_monitor',
      tool: corporateActionMonitor,
      description: 'Monitor Indian corporate actions using Drishti announcement categories, alerts, and optional news context.',
      compactDescription: 'Drishti corporate-action monitor: dividends, buybacks, M&A, management, orders, capex, legal/regulatory events.',
      concurrencySafe: true,
    },
    {
      name: 'governance_monitor',
      tool: governanceMonitor,
      description: 'Monitor Indian governance signals using Drishti management/promoter, compliance/legal, ownership/capital-action announcements, negative news, alerts, and concalls.',
      compactDescription: 'Drishti governance monitor: management, promoter, compliance, legal, ownership, controls, news, alerts.',
      concurrencySafe: true,
    },
    {
      name: 'risk_monitor',
      tool: riskMonitor,
      description: 'Monitor risk signals for Indian equities using Drishti negative news, legal/regulatory announcements, alerts, and concall red flags.',
      compactDescription: 'Drishti risk monitor: negative news, risk filings, alerts, and concall red flags.',
      concurrencySafe: true,
    },
    {
      name: 'event_calendar',
      tool: eventCalendar,
      description: 'Build a Drishti event calendar using upcoming earnings, upcoming concalls, meeting-intimation announcements, and recent result/call filings.',
      compactDescription: 'Drishti event calendar: upcoming earnings, concalls, meeting intimations, recent result/call filings.',
      concurrencySafe: true,
    },
    {
      name: 'get_financials',
      tool: createGetFinancials(model),
      description: GET_FINANCIALS_DESCRIPTION,
      compactDescription: 'Drishti Indian-equity company intelligence: symbol metadata, earnings, concalls, announcements, alerts.',
      concurrencySafe: true,
    },
    {
      name: 'get_market_data',
      tool: createGetMarketData(model),
      description: GET_MARKET_DATA_DESCRIPTION,
      compactDescription: 'Drishti Indian-market context: news, alerts, announcements, and watchlist summaries.',
      concurrencySafe: true,
    },
    {
      name: 'read_filings',
      tool: createReadFilings(model),
      description: READ_FILINGS_DESCRIPTION,
      compactDescription: 'Drishti Indian exchange filings: announcements, earnings filings, concalls, and attachment URLs.',
      concurrencySafe: true,
    },
    {
      name: 'spawn_subagent',
      tool: createSpawnSubagent(model),
      description: SPAWN_SUBAGENT_DESCRIPTION,
      compactDescription: 'Delegate a focused sub-task to an isolated subagent. Emit multiple calls in one turn to run independent sub-tasks in parallel.',
      concurrencySafe: true,
    },
    {
      name: 'ask_user_question',
      tool: createAskUserQuestion(),
      description: ASK_USER_QUESTION_DESCRIPTION,
      compactDescription: 'Ask the user 1-4 multiple-choice questions mid-turn and wait for their answers. CLI only.',
      concurrencySafe: false,
    },
    {
      name: 'web_fetch',
      tool: createWebFetch(model),
      description: WEB_FETCH_DESCRIPTION,
      compactDescription: 'Fetch a URL and answer a prompt about its content (HTML→markdown, fast-model summarized).',
      concurrencySafe: true,
    },
    {
      name: 'browser',
      tool: browserTool,
      description: BROWSER_DESCRIPTION,
      compactDescription: 'JavaScript-rendered pages and interactive navigation. Actions: navigate, snapshot, act, read, close.',
      concurrencySafe: true,
    },
    {
      name: 'read_file',
      tool: readFileTool,
      description: READ_FILE_DESCRIPTION,
      compactDescription: 'Read a local file by path. Returns file content as text.',
      concurrencySafe: true,
    },
    {
      name: 'write_file',
      tool: writeFileTool,
      description: WRITE_FILE_DESCRIPTION,
      compactDescription: 'Create or overwrite a file. Requires user approval.',
      concurrencySafe: false,
    },
    {
      name: 'edit_file',
      tool: editFileTool,
      description: EDIT_FILE_DESCRIPTION,
      compactDescription: 'Edit a file by replacing text. Requires user approval.',
      concurrencySafe: false,
    },
    {
      name: 'heartbeat',
      tool: heartbeatTool,
      description: HEARTBEAT_TOOL_DESCRIPTION,
      compactDescription: 'View or update the periodic heartbeat checklist (.max/HEARTBEAT.md).',
      concurrencySafe: true,
    },
    {
      name: 'cron',
      tool: cronTool,
      description: CRON_TOOL_DESCRIPTION,
      compactDescription: 'Manage scheduled cron jobs (create, list, update, delete).',
      concurrencySafe: true,
    },
    {
      name: 'memory_search',
      tool: memorySearchTool,
      description: MEMORY_SEARCH_DESCRIPTION,
      compactDescription: 'Search persistent memory and past conversations for stored facts and preferences.',
      concurrencySafe: true,
    },
    {
      name: 'memory_get',
      tool: memoryGetTool,
      description: MEMORY_GET_DESCRIPTION,
      compactDescription: 'Read specific memory file sections by line range.',
      concurrencySafe: true,
    },
    {
      name: 'memory_update',
      tool: memoryUpdateTool,
      description: MEMORY_UPDATE_DESCRIPTION,
      compactDescription: 'Add, edit, or delete persistent memory entries.',
      concurrencySafe: false,
    },
  ];

  // Build web_search as a fallback chain over whichever providers have keys configured.
  // The user's preferred provider (set via /search) is tried first; the others act as fallbacks.
  const allWebSearchProviders: WebSearchProvider[] = [];
  if (checkApiKeyExists('EXASEARCH_API_KEY')) {
    allWebSearchProviders.push({ id: 'exa', name: 'Exa', tool: exaSearch });
  }
  if (checkApiKeyExists('PERPLEXITY_API_KEY')) {
    allWebSearchProviders.push({ id: 'perplexity', name: 'Perplexity', tool: perplexitySearch });
  }
  if (checkApiKeyExists('TAVILY_API_KEY')) {
    allWebSearchProviders.push({ id: 'tavily', name: 'Tavily', tool: tavilySearch });
  }
  if (checkApiKeyExists('LANGSEARCH_API_KEY')) {
    allWebSearchProviders.push({ id: 'langsearch', name: 'LangSearch', tool: langSearch });
  }

  if (allWebSearchProviders.length > 0) {
    const preferred = getSetting<SearchProviderId | undefined>('webSearchPreferredProvider', undefined);
    const orderedProviders = preferred
      ? [
          ...allWebSearchProviders.filter((p) => p.id === preferred),
          ...allWebSearchProviders.filter((p) => p.id !== preferred),
        ]
      : allWebSearchProviders;

    tools.push({
      name: 'web_search',
      tool: createWebSearchTool(orderedProviders),
      description: WEB_SEARCH_DESCRIPTION,
      compactDescription: 'Search the web for current information. Returns titles, URLs, and snippets.',
      concurrencySafe: true,
    });
  }

  if (checkApiKeyExists('X_BEARER_TOKEN')) {
    tools.push({
      name: 'x_search',
      tool: xSearchTool,
      description: X_SEARCH_DESCRIPTION,
      compactDescription: 'Search X/Twitter for tweets, profiles, and threads.',
      concurrencySafe: true,
    });
  }

  // Drishti account status — useful whenever the REST key is present, even
  // without MCP. Lets the agent report credits/limits and explain quota errors.
  if (checkApiKeyExists('DRISHTI_API_KEY')) {
    tools.push({
      name: 'drishti_account_status',
      tool: createAccountStatusTool(),
      description: ACCOUNT_STATUS_DESCRIPTION,
      compactDescription: 'Report Drishti account state: credit balance, usage, rate limits, and warnings.',
      concurrencySafe: true,
    });
  }

  // MCP capability discovery. Individual MCP tools are NOT registered here —
  // they are surfaced on demand by search_tools and bound per-iteration.
  if (isMcpConfigured()) {
    tools.push({
      name: 'search_tools',
      tool: createSearchToolsTool(),
      description: SEARCH_TOOLS_DESCRIPTION,
      compactDescription: 'Discover Drishti MCP tools, including mandatory resolve_symbols for user-provided Indian equity names/tickers.',
      concurrencySafe: true,
    });
  }

  const availableSkills = discoverSkills();
  if (availableSkills.length > 0) {
    tools.push({
      name: 'skill',
      tool: skillTool,
      description: SKILL_TOOL_DESCRIPTION,
      compactDescription: 'Invoke a specialized skill workflow (e.g., investment memo).',
      concurrencySafe: false,
    });
  }

  return tools;
}

/**
 * Build a name → concurrencySafe map for the tool executor.
 */
export function getToolConcurrencyMap(model: string): Map<string, boolean> {
  return new Map(getToolRegistry(model).map(t => [t.name, t.concurrencySafe]));
}

/**
 * Get just the tool instances for binding to the LLM.
 *
 * @param model - The model name
 * @returns Array of tool instances
 */
export function getTools(model: string): StructuredToolInterface[] {
  return getToolRegistry(model).map((t) => t.tool);
}

/**
 * Build the tool descriptions section for the system prompt.
 * Formats each tool's rich description with a header.
 *
 * @param model - The model name
 * @returns Formatted string with all tool descriptions
 */
/**
 * Build compact tool descriptions for token-optimized system prompts.
 * Uses 1-2 sentence descriptions instead of full multi-paragraph ones.
 * The LLM already has full tool schemas via bindTools().
 */
export function buildCompactToolDescriptions(model: string): string {
  return getToolRegistry(model)
    .map((t) => `- **${t.name}**: ${t.compactDescription}`)
    .join('\n');
}
